import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import '@assets/css/common/style_main.css';
import '@assets/css/common/style_table.css';
import '@assets/css/common/style_modal.css';
import '@assets/css/common/style_calendar.css';
import PaginationZeroBased from '@components/common/PaginationZeroBased.jsx';

// 분리된 모달 컴포넌트들 import
import PhotoFilterInputModal from './components/PhotoFilterInputModal.jsx';
import PhotoFilterDetailModal from './components/PhotoFilterDetailModal.jsx';
import PhotoFilterConfirmModal from './components/PhotoFilterConfirmModal.jsx';
import photoFilterApi from '@services/photoFilterApi.jsx';
import useAuth from '@hooks/useAuth.jsx';

const PhotoFilterManagement = () => {
  const navigate = useNavigate();
  const { getUserFromToken } = useAuth();
  
  // 상태 관리
  const [photoFilters, setPhotoFilters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalPages: 0,
    totalElements: 0,
    size: 10,
    hasNext: false,
    hasPrevious: false
  });
  
  // 검색 및 필터 상태
  const [searchFilters, setSearchFilters] = useState({
    filterName: '',
    isDeployed: null,
    sortBy: 'createdAt',
    sortDirection: 'desc'
  });
  
  // 체크박스 상태 관리
  const [selectAll, setSelectAll] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  
  // 모달 상태 관리
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // 모달 메시지 상태
  const [alertMessage, setAlertMessage] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmCallback, setConfirmCallback] = useState(null);
  const [currentFilter, setCurrentFilter] = useState(null);
  
  // 모달 입력값 상태 관리
  const [modalInputs, setModalInputs] = useState({
    author: '',
    filterName: '',
    startDatetime: '',
    endDatetime: '',
    isDeployed: false,
    cameraData: '',
    filterDataFile: null,
    filterDataResourceId: null
  });
  
  // 지점 관련 상태
  const [availableBranches, setAvailableBranches] = useState([
    { id: 'branch-1', name: '신사점' }
  ]);
  
  const [selectedBranches, setSelectedBranches] = useState([
    { id: 'branch-default', name: '강남점' }
  ]);
  
  const [selectedAvailableBranch, setSelectedAvailableBranch] = useState(null);
  const [selectedAddedBranch, setSelectedAddedBranch] = useState(null);

  // 포토 필터 목록 조회
  const fetchPhotoFilters = useCallback(async (page = 0) => {
    setLoading(true);
    try {
      const params = {
        page,
        size: pagination.size,
        sortBy: searchFilters.sortBy,
        sortDirection: searchFilters.sortDirection,
        ...searchFilters
      };

      // 빈 값 필터링
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });

      const response = await photoFilterApi.getPhotoFilters(params);
      
      if (response.success) {
        // BaseResponse<Page<PhotoFilterResponse>> 구조 처리
        const pageData = response.data;
        setPhotoFilters(pageData.content || []);
        setPagination({
          currentPage: pageData.number || 0,
          totalPages: pageData.totalPages || 0,
          totalElements: pageData.totalElements || 0,
          size: pageData.size || 10,
          hasNext: pageData.hasNext || false,
          hasPrevious: pageData.hasPrevious || false
        });
      }
    } catch (error) {
      console.error('포토 필터 목록 조회 실패:', error);
      setAlertMessage('포토 필터 목록을 불러오는데 실패했습니다.');
      setShowAlertModal(true);
    } finally {
      setLoading(false);
    }
  }, [pagination.size, searchFilters]);

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    fetchPhotoFilters();
  }, []);

  // 필터 변경 시 첫 페이지로 이동하여 재조회
  useEffect(() => {
    if (pagination.currentPage !== 0) {
      fetchPhotoFilters(0);
    } else {
      fetchPhotoFilters();
    }
  }, [searchFilters]);

  // 체크박스 전체 선택/해제 핸들러
  const handleSelectAll = (e) => {
    setSelectAll(e.target.checked);
    setSelectedItems(e.target.checked ? photoFilters.map(filter => filter.id) : []);
  };
  
  // 개별 체크박스 핸들러
  const handleItemSelect = (id, e) => {
    if (e.target.checked) {
      setSelectedItems([...selectedItems, id]);
    } else {
      setSelectedItems(selectedItems.filter(item => item !== id));
    }
    
    // 전체 선택 상태 업데이트
    if (e.target.checked && selectedItems.length + 1 === photoFilters.length) {
      setSelectAll(true);
    } else if (!e.target.checked && selectAll) {
      setSelectAll(false);
    }
  };

  // 필터 변경 핸들러
  const handleFilterChange = (field, value) => {
    setSearchFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // 필터 추가 버튼 핸들러
  const handleAddFilterClick = () => {
    // JWT에서 사용자 정보 가져오기
    const user = getUserFromToken();
    
    setModalInputs({
      author: user?.name || 'system',
      filterName: '',
      startDatetime: '',
      endDatetime: '',
      isDeployed: false,
      cameraData: '',
      filterDataFile: null,
      filterDataResourceId: null
    });
    
    setAvailableBranches([
      { id: 'branch-1', name: '신사점' }
    ]);
    
    setSelectedBranches([
      { id: 'branch-default', name: '강남점' }
    ]);
    
    setSelectedAvailableBranch(null);
    setSelectedAddedBranch(null);
    
    setShowAddModal(true);
  };
  
  // 편집 버튼 핸들러
  const handleEditClick = (filter) => {
    setCurrentFilter(filter);
    
    // JWT에서 사용자 정보 가져오기
    const user = getUserFromToken();
    
    setModalInputs({
      author: user?.name || 'system',
      filterName: filter.filterName,
      startDatetime: formatDateForInput(filter.startDatetime),
      endDatetime: formatDateForInput(filter.endDatetime),
      isDeployed: filter.isDeployed || false,
      cameraData: filter.originalFilename || '',
      filterDataFile: null,
      filterDataResourceId: filter.filterDataResourceId || null
    });
    
    // 지점 정보 설정
    const branches = filter.branches || [];
    setSelectedBranches([
      { id: 'branch-default', name: '강남점' },
      ...branches.map(b => ({ id: b.branchId, name: b.branchName }))
    ]);
    
    setAvailableBranches([
      { id: 'branch-1', name: '신사점' }
    ]);
    
    setSelectedAvailableBranch(null);
    setSelectedAddedBranch(null);
    
    setShowEditModal(true);
  };
  
  // 삭제 버튼 핸들러
  const handleDeleteClick = (filter) => {
    setCurrentFilter(filter);
    setConfirmMessage(`${filter.filterName} 필터를 삭제하시겠습니까?`);
    setConfirmCallback(() => async () => {
      try {
        const response = await photoFilterApi.deletePhotoFilter(filter.id);
        if (response.success) {
          setAlertMessage(response.message || '삭제되었습니다.');
          setShowAlertModal(true);
          fetchPhotoFilters(pagination.currentPage);
        } else {
          setAlertMessage(response.message || '삭제에 실패했습니다.');
          setShowAlertModal(true);
        }
      } catch (error) {
        console.error('삭제 실패:', error);
        setAlertMessage('삭제에 실패했습니다.');
        setShowAlertModal(true);
      }
    });
    setShowConfirmModal(true);
  };
  
  // 중복 확인 핸들러
  const handleCheckDuplicate = async () => {
    const filterName = modalInputs.filterName.trim();
    if (!filterName) {
      setAlertMessage('필터명을 입력해주세요.');
      setShowAlertModal(true);
      return;
    }
    
    try {
      const excludeId = showEditModal && currentFilter ? currentFilter.id : null;
      const response = await photoFilterApi.checkFilterNameDuplicate(filterName, excludeId);
      
      if (response.success) {
        setAlertMessage(response.data.message || '중복 확인 완료');
      } else {
        setAlertMessage(response.message || '중복 확인 실패');
      }
      setShowAlertModal(true);
    } catch (error) {
      console.error('중복 확인 실패:', error);
      setAlertMessage('중복 확인에 실패했습니다.');
      setShowAlertModal(true);
    }
  };
  
  // 필터 추가/수정 확인 핸들러
  const handleConfirm = async () => {
    // 입력값 검증
    if (!modalInputs.filterName) {
      setAlertMessage('필터명을 입력해주세요.');
      setShowAlertModal(true);
      return;
    }
    
    if (!modalInputs.startDatetime || !modalInputs.endDatetime) {
      setAlertMessage('사용 기간을 선택해주세요.');
      setShowAlertModal(true);
      return;
    }

    try {
      // FormData 생성
      const requestData = {
        filterName: modalInputs.filterName,
        startDatetime: modalInputs.startDatetime,
        endDatetime: modalInputs.endDatetime,
        isDeployed: modalInputs.isDeployed,
        createdBy: modalInputs.author || 'system',
        updatedBy: modalInputs.author || 'system',
        filterDataResourceId: modalInputs.filterDataResourceId,
        // branches: selectedBranches
        //   .filter(b => b.id !== 'branch-default')
        //   .map(b => ({
        //     branchId: b.id,
        //     branchName: b.name,
        //     isActive: true
        //   }))
      };

      if (modalInputs.filterDataFile) {
        requestData.filterDataFile = modalInputs.filterDataFile;
      }

      const formData = requestData;

      if (showAddModal) {
        const response = await photoFilterApi.createPhotoFilter(formData);
        
        if (response.success) {
          setShowAddModal(false);
          
          // 필터 설정 확인
          setConfirmMessage('필터 설정을 진행하시겠습니까?');
          setConfirmCallback(() => () => {
            navigate(`/photo-filter-management/filter-setting?filter=${response.data.filterName}&author=${modalInputs.author}&date=${modalInputs.startDatetime}`);
          });
          setShowConfirmModal(true);
          
          fetchPhotoFilters(pagination.currentPage);
        } else {
          setAlertMessage(response.message || '생성에 실패했습니다.');
          setShowAlertModal(true);
        }
      } else if (showEditModal && currentFilter) {
        const response = await photoFilterApi.updatePhotoFilter(currentFilter.id, formData);
        
        if (response.success) {
          setAlertMessage(response.message || '필터 정보가 수정되었습니다.');
          setShowEditModal(false);
          setShowAlertModal(true);
          
          fetchPhotoFilters(pagination.currentPage);
        } else {
          setAlertMessage(response.message || '수정에 실패했습니다.');
          setShowAlertModal(true);
        }
      }
    } catch (error) {
      console.error('처리 실패:', error);
      setAlertMessage('처리 중 오류가 발생했습니다.');
      setShowAlertModal(true);
    }
  };

  // 일괄 삭제 핸들러
  const handleBulkDelete = () => {
    if (selectedItems.length === 0) return;
    
    setConfirmMessage(`선택한 ${selectedItems.length}개의 필터를 삭제하시겠습니까?`);
    setConfirmCallback(() => async () => {
      try {
        const response = await photoFilterApi.deletePhotoFilters(selectedItems);
        if (response.success) {
          setSelectedItems([]);
          setSelectAll(false);
          setAlertMessage(response.message || response.data || '선택한 필터가 삭제되었습니다.');
          setShowAlertModal(true);
          fetchPhotoFilters(pagination.currentPage);
        } else {
          setAlertMessage(response.message || '삭제에 실패했습니다.');
          setShowAlertModal(true);
        }
      } catch (error) {
        console.error('일괄 삭제 실패:', error);
        setAlertMessage('삭제에 실패했습니다.');
        setShowAlertModal(true);
      }
    });
    setShowConfirmModal(true);
  };

  // 페이지 변경 핸들러
  const handlePageChange = (page) => {
    fetchPhotoFilters(page);
  };

  // 파일 불러오기 핸들러 (기존 방식)
  const handleFileImport = () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.csv,.xlsx,.xls';
    
    fileInput.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        setModalInputs(prev => ({
          ...prev,
          filterDataFile: file,
          cameraData: file.name
        }));
        
        setAlertMessage(`파일 "${file.name}"이(가) 선택되었습니다.`);
        setShowAlertModal(true);
      }
    };
    
    fileInput.click();
  };

  // 리소스 업로드 핸들러 (공통 방식)
  const handleResourceUploaded = (resourceId) => {
    setModalInputs(prev => ({
      ...prev,
      filterDataResourceId: resourceId
    }));
  };

  // 날짜 포맷팅 헬퍼
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}${month}${day}`;
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '무제한';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}${month}${day}`;
  };

  // 지점 선택 핸들러
  const handleBranchSelect = (type, id) => {
    if (type === 'available') {
      setSelectedAvailableBranch(id);
      setSelectedAddedBranch(null);
    } else {
      setSelectedAddedBranch(id);
      setSelectedAvailableBranch(null);
    }
  };
  
  // 지점 추가 핸들러
  const handleAddBranch = (branchesToAdd) => {
    if (!branchesToAdd || branchesToAdd.length === 0) return;
    
    if (typeof branchesToAdd === 'string') {
      const branchToAdd = availableBranches.find(b => b.id === branchesToAdd);
      if (!branchToAdd) return;
      
      const isDuplicate = selectedBranches.some(b => b.id === branchToAdd.id);
      if (isDuplicate) return;
      
      setSelectedBranches([...selectedBranches, branchToAdd]);
      setSelectedAvailableBranch(null);
      return;
    }
    
    const newBranches = branchesToAdd.filter(branch => 
      !selectedBranches.some(b => b.id === branch.id)
    );
    
    if (newBranches.length > 0) {
      setSelectedBranches([...selectedBranches, ...newBranches]);
    }
  };
  
  // 지점 제거 핸들러
  const handleRemoveBranch = (branchIds) => {
    if (!branchIds || branchIds.length === 0) return;
    
    if (typeof branchIds === 'string') {
      if (branchIds === 'branch-default') return;
      
      setSelectedBranches(selectedBranches.filter(b => b.id !== branchIds));
      setSelectedAddedBranch(null);
      return;
    }
    
    setSelectedBranches(selectedBranches.filter(b => !branchIds.includes(b.id)));
  };
  
  // 상세정보 아이콘 클릭 핸들러
  const handleInfoClick = (filter) => {
    setCurrentFilter(filter);
    setShowDetailModal(true);
  };

  return (
    <>
      <div className="page-title-container">
        <h1 className="page-title">포토 필터</h1>
        <div className="page-title-icon">
          <a href="#" id="add-filter-btn" title="포토 필터 추가" onClick={handleAddFilterClick}>
            <i className="ri-add-circle-line"></i>
          </a>
        </div>
      </div>
      
      <div className="page-container">
        
        {/* 필터 드롭다운 */}
        <div className="filter-dropdown-container-row">
          {/* 일괄 삭제 버튼 */}
          <div className={`bulk-delete-container ${selectedItems.length >= 2 ? 'active' : ''}`}>
            <button className="bulk-delete-btn" onClick={handleBulkDelete}>
              일괄 삭제 ({selectedItems.length})
            </button>
          </div>

          <div className="filter-dropdown">
            <select 
              value={searchFilters.sortBy} 
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
            >
              <option value="createdAt">최신순</option>
              <option value="filterName">이름순</option>
              <option value="startDatetime">사용기간순</option>
            </select>
          </div>
        </div>
        
        {/* 필터 테이블 */}
        <div className="common-table-container">
          {loading ? (
            <div className="loading-container">
              <p>로딩 중...</p>
            </div>
          ) : (
            <table className="common-table">
              <thead>
                <tr>
                  <th>
                    <div className="checkbox-container">
                      <input 
                        type="checkbox" 
                        id="select-all" 
                        checked={selectAll}
                        onChange={handleSelectAll}
                      />
                      <label htmlFor="select-all" className="custom-checkbox"></label>
                    </div>
                  </th>
                  <th>NO</th>
                  <th>필터명</th>
                  <th>사용 기간</th>
                  <th>배포 여부</th>
                  <th>상세정보</th>
                  <th>수정</th>
                  <th>삭제</th>
                </tr>
              </thead>
              <tbody>
                {photoFilters.map((filter, index) => (
                  <tr key={filter.id}>
                    <td>
                      <div className="checkbox-container">
                        <input 
                          type="checkbox" 
                          id={`filter-${filter.id}`}
                          checked={selectedItems.includes(filter.id)}
                          onChange={(e) => handleItemSelect(filter.id, e)}
                        />
                        <label htmlFor={`filter-${filter.id}`} className="custom-checkbox"></label>
                      </div>
                    </td>
                    <td>{pagination.currentPage * pagination.size + index + 1}</td>
                    <td>{filter.filterName}</td>
                    <td className="usage-period">
                      {formatDateForDisplay(filter.startDatetime)} - {formatDateForDisplay(filter.endDatetime)}
                    </td>
                    <td>{filter.isDeployed ? 'Y' : 'N'}</td>
                    <td>
                      <button className="info-btn" onClick={() => handleInfoClick(filter)}>
                        <i className="ri-information-line"></i>
                      </button>
                    </td>
                    <td>
                      <button className="edit-btn" onClick={() => handleEditClick(filter)}>
                        <i className="ri-pencil-line"></i>
                      </button>
                    </td>
                    <td>
                      <button className="delete-btn" onClick={() => handleDeleteClick(filter)}>
                        <i className="ri-close-line"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        
        {/* 페이지네이션 */}
        <PaginationZeroBased
          page={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalElements={pagination.totalElements}
          size={pagination.size}
          hasNext={pagination.hasNext}
          hasPrevious={pagination.hasPrevious}
          onPageChange={handlePageChange}
        />
      </div>
      
      {/* 필터 추가 모달 */}
      <PhotoFilterInputModal
        title="포토 필터 추가"
        isVisible={showAddModal}
        inputs={modalInputs}
        setInputs={setModalInputs}
        onClose={() => setShowAddModal(false)}
        onConfirm={handleConfirm}
        onCheckDuplicate={handleCheckDuplicate}
        availableBranches={availableBranches}
        selectedBranches={selectedBranches}
        onAddBranch={handleAddBranch}
        onRemoveBranch={handleRemoveBranch}
        onBranchSelect={handleBranchSelect}
        selectedAvailableBranch={selectedAvailableBranch}
        selectedAddedBranch={selectedAddedBranch}
        isEdit={false}
        onFileImport={handleFileImport}
        onResourceUploaded={handleResourceUploaded}
      />
      
      {/* 필터 수정 모달 */}
      <PhotoFilterInputModal
        title="포토 필터 수정"
        isVisible={showEditModal}
        inputs={modalInputs}
        setInputs={setModalInputs}
        onClose={() => setShowEditModal(false)}
        onConfirm={handleConfirm}
        onCheckDuplicate={handleCheckDuplicate}
        availableBranches={availableBranches}
        selectedBranches={selectedBranches}
        onAddBranch={handleAddBranch}
        onRemoveBranch={handleRemoveBranch}
        onBranchSelect={handleBranchSelect}
        selectedAvailableBranch={selectedAvailableBranch}
        selectedAddedBranch={selectedAddedBranch}
        isEdit={true}
        onFileImport={handleFileImport}
        onResourceUploaded={handleResourceUploaded}
      />
      
      {/* 상세정보 모달 */}
      <PhotoFilterDetailModal
        isVisible={showDetailModal}
        photoFilter={currentFilter}
        onClose={() => setShowDetailModal(false)}
      />
      
      {/* 알림 모달 */}
      <PhotoFilterConfirmModal
        isVisible={showAlertModal}
        title="알림"
        message={alertMessage}
        type="alert"
        onConfirm={() => setShowAlertModal(false)}
        onCancel={() => setShowAlertModal(false)}
      />
      
      {/* 확인 모달 */}
      <PhotoFilterConfirmModal
        isVisible={showConfirmModal}
        title="확인"
        message={confirmMessage}
        type="confirm"
        onConfirm={() => {
          if (confirmCallback) {
            confirmCallback();
          }
          setShowConfirmModal(false);
        }}
        onCancel={() => setShowConfirmModal(false)}
        confirmText="예"
        cancelText="아니오"
      />
    </>
  );
};

export default PhotoFilterManagement;
